import { useEffect } from "react";
import { toast, type ToastOptions } from "react-toastify";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CallDisabledIcon,
  MicOff01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize } from "shared/components";
import { cx } from "shared/utils";
import { useSoftphone } from "./SoftphoneProvider";

const DISCONNECTED_TOAST_ID = "SOFTPHONE_DISCONNECTED";
const MIC_DENIED_TOAST_ID = "SOFTPHONE_MIC_DENIED";

// Persistent, manually dismissable toast styled after the app's standard
// action toast (see TaskTable's "Task deleted / Undo"). Rendered through the
// global ToastContainer so it inherits the design-system look instead of the
// old ad-hoc fixed banner (CRM-1073).
const PERSISTENT_TOAST_OPTIONS: ToastOptions = {
  autoClose: false,
  closeOnClick: false,
  closeButton: true,
  icon: false,
  className: "!bg-background-1000 border border-alpha-100 shadow-lg rounded-lg",
  bodyClassName: "p-2"
};

const SoftphoneToastBody = ({
  icon,
  accentClassName,
  title,
  body,
  actionLabel,
  onAction
}: {
  icon: typeof CallDisabledIcon;
  accentClassName: string;
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
}) => (
  <div className="flex w-full items-center justify-between gap-4">
    <div className="flex items-start gap-2">
      <HugeiconsIcon
        icon={icon}
        size={20}
        className={cx("mt-0.5 shrink-0", accentClassName)}
      />
      <div>
        <p className="text-base font-bold text-content-1000">{title}</p>
        <p className="mt-0.5 text-sm text-content-500">{body}</p>
      </div>
    </div>
    <Button size={ButtonSize.SM} onClick={onAction}>
      {actionLabel}
    </Button>
  </div>
);

/**
 * Surfaces the softphone's terminal device states ("error", "mic-denied") as
 * persistent toasts with a recovery action. Mounted once by CallOverlay; renders
 * nothing itself — the global ToastContainer owns the presentation.
 */
export function SoftphoneToasts() {
  const { status, errorMessage, retryRegistration } = useSoftphone();

  useEffect(() => {
    if (status !== "error") toast.dismiss(DISCONNECTED_TOAST_ID);
    if (status !== "mic-denied") toast.dismiss(MIC_DENIED_TOAST_ID);

    if (status === "error")
      toast(
        ({ closeToast }) => (
          <SoftphoneToastBody
            icon={CallDisabledIcon}
            accentClassName="text-danger-600"
            title="Voice service disconnected"
            body={errorMessage ?? "The softphone lost its connection."}
            actionLabel="Reconnect"
            onAction={() => {
              retryRegistration();
              closeToast();
            }}
          />
        ),
        { ...PERSISTENT_TOAST_OPTIONS, toastId: DISCONNECTED_TOAST_ID }
      );

    if (status === "mic-denied")
      toast(
        ({ closeToast }) => (
          <SoftphoneToastBody
            icon={MicOff01Icon}
            accentClassName="text-warning-600"
            title="Microphone access is required"
            body="Click the lock icon next to the URL, allow microphone access, then retry."
            actionLabel="Retry"
            onAction={() => {
              retryRegistration();
              closeToast();
            }}
          />
        ),
        { ...PERSISTENT_TOAST_OPTIONS, toastId: MIC_DENIED_TOAST_ID }
      );
  }, [status, errorMessage, retryRegistration]);

  // Don't leave an orphaned toast behind if the whole overlay unmounts
  // (e.g. the softphone feature is toggled off).
  useEffect(
    () => () => {
      toast.dismiss(DISCONNECTED_TOAST_ID);
      toast.dismiss(MIC_DENIED_TOAST_ID);
    },
    []
  );

  return null;
}
